import { Component, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BreadcrumbService } from 'xng-breadcrumb';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ReportingService } from 'src/app/shared/store/reporting/reporting.service';
import {
  CallsPerformanceQuery,
  CallsPerformanceReportItem,
} from 'src/app/shared/store/reporting/types';
import { ViewService } from '../../../../shared/store/view/views.service';
import { NavigationMemoryService } from '../../../../shared/store/navigation-memory/navigation-memory.service';
import { CALLS_FACET_ALL_ID } from './telephony-comparison-calls-subheader/telephony-comparison-calls-subheader.component';

/** Subheader selection persisted across a drill-down round-trip. */
interface TelephonyComparisonCallsFilters {
  period: string;
  directionIds: number[];
  salesTeamIds: number[];
  salesPersonIds: number[];
}

/**
 * Calls dashboard — one row per (salesperson, office) over the selected period, then the former
 * employees, the calls with no salesperson and a "Total" row.
 *
 * Fed by `/api/reporting/callsPerformance` with the query the subheader builds for its own
 * dropdowns, so the rows and the dropdown counts cover the same calls. Rates are computed
 * server-side.
 */
@UntilDestroy()
@Component({
  selector: 'app-telephony-comparison-calls-dashboard',
  templateUrl: './telephony-comparison-calls-dashboard.component.html',
  styleUrls: ['./telephony-comparison-calls-dashboard.component.scss'],
  standalone: false,
})
export class TelephonyComparisonCallsDashboardComponent implements OnInit {
  // --- subheader state, kept here for the navigation memory ---
  // Start on "All": an empty selection would be defaulted by the subheader during its init, changing
  // this component's bindings after they were checked (NG0100).
  period: string = 'CurrentMonth';
  directionIds: number[] = [CALLS_FACET_ALL_ID];
  salesTeamIds: number[] = [CALLS_FACET_ALL_ID];
  salesPersonIds: number[] = [CALLS_FACET_ALL_ID];

  /** Identifies this dashboard's slot in the shared navigation-memory store. */
  private readonly memoryKey = 'telephonyComparisonCalls';

  callsData$: Observable<CallsPerformanceReportItem[]>;

  constructor(
    private readonly reportingService: ReportingService,
    private readonly viewService: ViewService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly router: Router,
    private readonly navigationMemoryService: NavigationMemoryService,
  ) {
    this.callsData$ = this.reportingService.getCallsPerformance.value$;
  }

  ngOnInit(): void {
    // Restore the subheader selection on a direct return from a drill-down (synchronous read: the
    // store emits its current value on subscribe, before the subheader initialises its inputs).
    this.navigationMemoryService
      .getMemory()
      .pipe(take(1))
      .subscribe((memory) => {
        if (
          this.navigationMemoryService.isValid(
            memory,
            this.memoryKey,
            this.router.url,
          )
        ) {
          const payload = memory.payload as TelephonyComparisonCallsFilters;
          this.period = payload.period;
          this.directionIds = payload.directionIds;
          this.salesTeamIds = payload.salesTeamIds;
          this.salesPersonIds = payload.salesPersonIds;
        }
        this.navigationMemoryService.reset();
      });

    // Arm the memory only on a real drill-down (the smart-action-link sets the `useTempFilter`
    // navigation state); the target URL is whatever it routes to.
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        untilDestroyed(this),
      )
      .subscribe((e) => {
        const state = this.router.getCurrentNavigation()?.extras.state;
        if (state && state['useTempFilter'] === true) {
          this.navigationMemoryService.keep<TelephonyComparisonCallsFilters>(
            this.memoryKey,
            {
              period: this.period,
              directionIds: this.directionIds,
              salesTeamIds: this.salesTeamIds,
              salesPersonIds: this.salesPersonIds,
            },
            this.router.url,
            e.url,
          );
        }
      });

    this.viewService.getViews.value$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((view) => {
        this.breadcrumbService.set('@salesDashboard', view.title);
      });
  }

  onQueryChange(query: CallsPerformanceQuery) {
    this.reportingService.getCallsPerformance.call(query);
  }
}
